import { Component, OnInit } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { StaffSessionService } from '../services/staff-session.service';
import { StaffProfileService, StaffTeachingClass } from '../services/staff-profile.service';

declare var customToastr: any;
function safeToast(message: string, type: 'success' | 'error' | 'info' = 'info') {
  if (typeof customToastr === 'function') { customToastr(message, type); }
}

@Component({
  selector: 'app-staff-profile',
  templateUrl: './staff-profile.component.html',
  styleUrls: ['../shared/staff-common.css', './staff-profile.component.css']
})
export class StaffProfileComponent implements OnInit {
  loading = true;
  loadError = false;

  staff = {
    name: '',
    initials: '',
    role: '',
    employeeId: '',
    schoolName: '',
    staffImage: '',
    email: '',
    phone: '',
    address: '',
    gender: '',
    dateOfBirth: 'N/A',
    dateOfJoining: 'N/A',
    isClassTeacher: false,
  };

  teachingClasses: StaffTeachingClass[] = [];

  constructor(
    private staffSession: StaffSessionService,
    private staffProfileService: StaffProfileService,
    private translate: TranslateService
  ) {}

  ngOnInit(): void {
    // Instant paint from whatever's already cached (fresher class-list info,
    // falling back to the OTP-login response), then the real API response
    // below overwrites every field it actually provides.
    const info = this.staffSession.getSchoolAndStaffInfo();
    const staffDetails = this.staffSession.getStaffDetails();
    const name = info?.staffName || staffDetails?.staffName || '';
    if (name) {
      this.staff.name = name;
      this.staff.initials = this.staffSession.getInitials(name);
      this.staff.role = info?.designation || staffDetails?.designation || '';
      this.staff.employeeId = info?.employmentId || staffDetails?.employmentId || '';
      this.staff.email = info?.emailId || staffDetails?.emailId || '';
      this.staff.phone = info?.mobileNumber || staffDetails?.mobileNumber || '';
      this.staff.schoolName = info?.schoolName || staffDetails?.schoolName || '';
      this.staff.staffImage = info?.staffImage || '';
      this.staff.address = info?.address || '';
    }
    // isClassTeacher reflects the viewer's own status from the freshest
    // cache available — accurate here since this page only ever shows the
    // logged-in staff member's own profile (no "view another staff" flow).
    this.staff.isClassTeacher = !!(info?.isClassTeacher ?? staffDetails?.isClassTeacher);

    this.loadProfile();
  }

  private async loadProfile() {
    const staffDetails = this.staffSession.getStaffDetails();
    if (!staffDetails) {
      this.loading = false;
      this.loadError = true;
      return;
    }

    this.loading = true;
    this.loadError = false;
    try {
      const response = await firstValueFrom(this.staffProfileService.getTeacherInfo(staffDetails.staffId));
      const responseDetails = response as any;
      const profile = responseDetails?.profile;
      if (!profile) {
        this.loadError = true;
        return;
      }

      this.staff.name = profile.staffName || this.staff.name;
      this.staff.initials = this.staffSession.getInitials(this.staff.name);
      this.staff.role = profile.designation || this.staff.role;
      this.staff.employeeId = profile.employmentId || this.staff.employeeId;
      this.staff.email = profile.emailId || this.staff.email;
      this.staff.phone = profile.mobileNumber || this.staff.phone;
      this.staff.staffImage = profile.staffImage || this.staff.staffImage;
      this.staff.address = profile.address || this.staff.address;
      this.staff.gender = profile.gender || '';
      this.staff.dateOfBirth = this.formatDate(profile.dateOfBirth);
      this.staff.dateOfJoining = this.formatJoiningDate(profile.dateOfJoining);

      this.teachingClasses = responseDetails.teachingClassesAndSubjects || [];
    } catch (error) {
      this.loadError = true;
      const unableToLoadProfileTranslation = await firstValueFrom(this.translate.get('unableToLoadProfilePleaseTryAgain'));
      safeToast(unableToLoadProfileTranslation, 'error');
    } finally {
      this.loading = false;
    }
  }

  private formatDate(value: string | null | undefined): string {
    if (!value) { return 'N/A'; }
    const d = new Date(value);
    if (isNaN(d.getTime())) { return 'N/A'; }
    return d.toLocaleDateString('en-US', { day: 'numeric', month: 'short', year: 'numeric' });
  }

  // dateOfJoining can come back as the sentinel "0000-00-00" for staff with
  // no recorded joining date — treat that identically to a missing value.
  private formatJoiningDate(value: string | null | undefined): string {
    if (!value || value === '0000-00-00') { return 'N/A'; }
    return this.formatDate(value);
  }

  classLabel(c: StaffTeachingClass): string {
    return `${c.classNumber}-${c.classDivision} — ${c.subject}`;
  }

  logout() {
    this.staffSession.logout();
  }
}
